import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const Toast = ({ toast, onRemove }) => {
  useEffect(() => {
    if (toast.autoClose) {
      const timer = setTimeout(() => {
        onRemove(toast.id);
      }, toast.duration || 4000);
      return () => clearTimeout(timer);
    }
  }, [toast, onRemove]);

  const getIcon = () => {
    if (toast.type === 'success') {
      return (
        <div className="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    }
    if (toast.type === 'error') {
      return (
        <div className="w-9 h-9 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      );
    }
    if (toast.type === 'info') {
      return (
        <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 400, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 400, scale: 0.96 }}
      transition={{ type: 'spring', damping: 22, stiffness: 300 }}
      className="relative bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden min-w-[280px] max-w-[380px]"
    >
      <div className="flex items-start gap-3 p-4">
        {getIcon()}
        
        <div className="flex-1 min-w-0 pt-0.5">
          {toast.title && (
            <h4 className="font-semibold text-gray-900 mb-0.5 text-sm leading-tight">{toast.title}</h4>
          )}
          <p className="text-sm text-gray-600 leading-relaxed">{toast.message}</p>
        </div>
        
        <button
          onClick={() => onRemove(toast.id)}
          className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors mt-0.5"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>
    </motion.div>
  );
};

export const ToastContainer = ({ toasts, onRemove }) => {
  // Only show the most recent toast (single toast at a time)
  const currentToast = toasts.length > 0 ? toasts[toasts.length - 1] : null;
  
  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2.5 pointer-events-none">
      <AnimatePresence mode="wait">
        {currentToast && (
          <div key={currentToast.id} className="pointer-events-auto">
            <Toast toast={currentToast} onRemove={onRemove} />
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Toast;
